import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Clock, Euro } from "lucide-react";
import api from "../api";
import { APPLICATION_STATUS_LABELS } from "../lib/constants";

export default function MijnSollicitaties() {
  const [apps, setApps] = useState(null);

  useEffect(() => {
    api.get("/applications/mine").then(({ data }) => setApps(data)).catch(() => setApps([]));
  }, []);

  if (apps === null) return <div className="ok-container py-20 text-sm">Laden...</div>;

  const grouped = {
    ingediend: apps.filter((a) => a.status === "ingediend"),
    aangeboden: apps.filter((a) => a.status === "aangeboden"),
    aangenomen: apps.filter((a) => a.status === "aangenomen"),
    afgewezen: apps.filter((a) => a.status === "afgewezen"),
  };

  return (
    <section className="ok-section">
      <div className="ok-container">
        <Link
          to="/kandidaat-dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-[#4a5d5e] hover:text-[#01696f]"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={14} /> Terug naar dashboard
        </Link>
        <div className="mt-6">
          <div className="ok-eyebrow mb-2">Sollicitatiegeschiedenis</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Mijn sollicitaties</h1>
          <p className="text-[#4a5d5e] mt-3 max-w-2xl">
            Volledig overzicht van alle vacatures waarop je hebt gereageerd, inclusief huidige status.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Stat label="In behandeling" value={grouped.ingediend.length} />
          <Stat label="Aangeboden" value={grouped.aangeboden.length} />
          <Stat label="Aangenomen" value={grouped.aangenomen.length} accent />
          <Stat label="Afgewezen" value={grouped.afgewezen.length} />
        </div>

        <div className="mt-12">
          {apps.length === 0 ? (
            <div className="border border-dashed border-[#e2e8f0] p-12 text-center" data-testid="no-applications">
              <p className="text-[#4a5d5e]">Je hebt nog geen sollicitaties verstuurd.</p>
              <Link
                to="/vacatures"
                className="inline-flex items-center gap-2 mt-6 bg-[#01696f] text-white px-6 py-3 rounded-sm hover:bg-[#015257] font-medium"
              >
                Bekijk open vacatures
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {apps.map((a) => (
                <div
                  key={a.id}
                  data-testid={`application-card-${a.id}`}
                  className="border border-[#e2e8f0] p-5 md:p-6 hover:border-[#01696f] transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1">
                      <span className={`ok-chip ${a.status === "aangenomen" ? "bg-[#01696f] text-white border-[#01696f]" : ""}`}>
                        {APPLICATION_STATUS_LABELS[a.status] || a.status}
                      </span>
                      <h3 className="font-display text-xl font-semibold mt-3">
                        {a.vacancy ? (
                          <Link to={`/vacature/${a.vacancy_id}`} className="hover:text-[#01696f]">
                            {a.vacancy.title}
                          </Link>
                        ) : (
                          <span className="text-[#7b8e8f]">Vacature niet meer beschikbaar</span>
                        )}
                      </h3>
                      {a.vacancy && (
                        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#4a5d5e]">
                          <span className="inline-flex items-center gap-1"><MapPin size={12} />{a.vacancy.location}</span>
                          <span className="inline-flex items-center gap-1"><Clock size={12} />{a.vacancy.hours_per_week} uur/wk</span>
                          {a.vacancy.hourly_rate_max && (
                            <span className="inline-flex items-center gap-1"><Euro size={12} />tot € {a.vacancy.hourly_rate_max}/u</span>
                          )}
                          <span className="inline-flex items-center gap-1"><Calendar size={12} />ingediend {a.submitted_date?.slice(0, 10)}</span>
                        </div>
                      )}
                      {a.notes && <p className="text-sm text-[#4a5d5e] mt-3">{a.notes}</p>}
                    </div>
                    {a.vacancy && (
                      <Link
                        to={`/vacature/${a.vacancy_id}`}
                        data-testid={`view-vacancy-${a.id}`}
                        className="text-sm text-[#01696f] font-semibold hover:underline whitespace-nowrap"
                      >
                        Bekijk vacature →
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`border border-[#e2e8f0] p-5 ${accent ? "bg-[#e6f0f1] border-[#01696f]" : ""}`}>
      <div className="text-xs uppercase tracking-wider text-[#4a5d5e]">{label}</div>
      <div className={`font-display text-3xl font-bold mt-2 ${accent ? "text-[#01696f]" : "text-[#0a191a]"}`}>{value}</div>
    </div>
  );
}
